import * as THREE from 'three';
import { BlendMode, MeshBasicNodeMaterial } from 'three/webgpu';
import {
  bitAnd,
  float,
  instanceIndex,
  mrt,
  output,
  positionLocal,
  select,
  shiftRight,
  storage,
  uint,
  vec3,
  vec4,
} from 'three/tsl';

const selectAny = select as any;
import type { PhysicsEngine } from '../physics/PhysicsEngine';
import { registerGtaoPresentationOverlayMaterial } from './gtaoPresentationOverlay';
import {
  CONTACT_RECORD_ARM_A_OFFSET,
  CONTACT_RECORD_ARM_B_OFFSET,
  CONTACT_RECORD_META_OFFSET,
  CONTACT_RECORD_VEC4S,
} from '../physics/gpu/contactRecord';

export class ContactPointOverlay {
  private static readonly MAX_DEBUG_CONTACTS = 4096;
  private pointAMesh: THREE.InstancedMesh | null = null;
  private pointBMesh: THREE.InstancedMesh | null = null;
  private connectorMesh: THREE.InstancedMesh | null = null;
  private pointGeometry: THREE.SphereGeometry | null = null;
  private connectorGeometry: THREE.CylinderGeometry | null = null;
  private readonly capacity: number;
  private pointAMaterial: MeshBasicNodeMaterial | null = null;
  private pointBMaterial: MeshBasicNodeMaterial | null = null;
  private connectorMaterial: MeshBasicNodeMaterial | null = null;
  private readonly scene: THREE.Object3D;
  private enabled = false;
  private gpuVisualsBound = false;

  constructor(scene: THREE.Object3D, capacity: number) {
    this.scene = scene;
    // This is a diagnostic view, not a second full-size contact renderer.
    // Display a bounded prefix of the already compact active-contact list.
    this.capacity = Math.max(1, Math.min(capacity, ContactPointOverlay.MAX_DEBUG_CONTACTS));
  }

  private ensureMeshes(): void {
    if (this.pointAMesh && this.pointBMesh && this.connectorMesh) return;

    const pointGeometry = new THREE.SphereGeometry(1.0, 10, 10);
    const connectorGeometry = new THREE.CylinderGeometry(1.0, 1.0, 1.0, 8, 1, true);
    this.pointGeometry = pointGeometry;
    this.connectorGeometry = connectorGeometry;

    const pointAMaterial = new MeshBasicNodeMaterial();
    pointAMaterial.color.setHex(0xffcf56);
    pointAMaterial.transparent = true;
    pointAMaterial.opacity = 0.98;
    pointAMaterial.depthWrite = false;
    // Debug contacts are transparent overlays, not occluding geometry. Preserve
    // the opaque GTAO guide attachment while still blending their scene color.
    const pointAMrt = mrt({ output, normal: vec4(0, 0, 0, 0) });
    pointAMrt.setBlendMode('normal', new BlendMode(THREE.MaterialBlending));
    registerGtaoPresentationOverlayMaterial(pointAMaterial, pointAMrt);
    this.pointAMaterial = pointAMaterial;

    const pointBMaterial = new MeshBasicNodeMaterial();
    pointBMaterial.color.setHex(0xd62939);
    pointBMaterial.transparent = true;
    pointBMaterial.opacity = 0.98;
    pointBMaterial.depthWrite = false;
    const pointBMrt = mrt({ output, normal: vec4(0, 0, 0, 0) });
    pointBMrt.setBlendMode('normal', new BlendMode(THREE.MaterialBlending));
    registerGtaoPresentationOverlayMaterial(pointBMaterial, pointBMrt);
    this.pointBMaterial = pointBMaterial;

    const connectorMaterial = new MeshBasicNodeMaterial();
    connectorMaterial.color.setHex(0xff8c42);
    connectorMaterial.transparent = true;
    connectorMaterial.opacity = 0.32;
    connectorMaterial.depthWrite = false;
    const connectorMrt = mrt({ output, normal: vec4(0, 0, 0, 0) });
    connectorMrt.setBlendMode('normal', new BlendMode(THREE.MaterialBlending));
    registerGtaoPresentationOverlayMaterial(connectorMaterial, connectorMrt);
    this.connectorMaterial = connectorMaterial;

    this.connectorMesh = new THREE.InstancedMesh(connectorGeometry, connectorMaterial, this.capacity);
    this.connectorMesh.count = 0;
    this.connectorMesh.frustumCulled = false;
    this.connectorMesh.renderOrder = 3;
    this.connectorMesh.visible = false;
    this.scene.add(this.connectorMesh);

    this.pointAMesh = new THREE.InstancedMesh(pointGeometry, pointAMaterial, this.capacity);
    this.pointAMesh.count = 0;
    this.pointAMesh.frustumCulled = false;
    this.pointAMesh.renderOrder = 4;
    this.pointAMesh.visible = false;
    this.scene.add(this.pointAMesh);

    this.pointBMesh = new THREE.InstancedMesh(pointGeometry, pointBMaterial, this.capacity);
    this.pointBMesh.count = 0;
    this.pointBMesh.frustumCulled = false;
    this.pointBMesh.renderOrder = 5;
    this.pointBMesh.visible = false;
    this.scene.add(this.pointBMesh);
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (!enabled) {
      this.clear();
      return;
    }
    this.ensureMeshes();
    this.connectorMesh!.visible = true;
    this.pointAMesh!.visible = true;
    this.pointBMesh!.visible = true;
  }

  clear(): void {
    if (this.connectorMesh) this.connectorMesh.visible = false;
    if (this.pointAMesh) this.pointAMesh.visible = false;
    if (this.pointBMesh) this.pointBMesh.visible = false;
  }

  requestUpdate(physics: PhysicsEngine): void {
    if (!this.enabled) return;
    this.ensureMeshes();
    if (!this.gpuVisualsBound) {
      const renderBuffers = physics.getContactRenderBuffers();
      if (!renderBuffers) return;

      const positions = storage(renderBuffers.positions, 'vec4', physics.config.maxBodies).toReadOnly();
      const quaternions = storage(renderBuffers.quaternions, 'vec4', physics.config.maxBodies).toReadOnly();
      const pairContacts = storage(
        renderBuffers.pairContacts,
        'vec4',
        renderBuffers.maxPairContacts * CONTACT_RECORD_VEC4S,
      ).toReadOnly();
      const pairActivity = storage(renderBuffers.pairActivity, 'uint', renderBuffers.pairActivityWordCount).toReadOnly();

      const idx = instanceIndex;
      const activeCount = pairActivity.element(uint(renderBuffers.pairActiveContactsOffset));
      const isListed = idx.lessThan(activeCount);
      const pairSlotWord = pairActivity.element(idx.add(uint(renderBuffers.pairActiveContactsOffset + 1)));
      const pairIndex = selectAny(isListed, pairSlotWord, uint(0));

      const contactBase = pairIndex.mul(uint(CONTACT_RECORD_VEC4S));
      const meta = pairContacts.element(contactBase.add(uint(CONTACT_RECORD_META_OFFSET)));
      const rowActive = meta.z.greaterThanEqual(0.5);
      const isVisible = isListed.and(rowActive);

      const iIndex = uint(meta.x);
      const jIndex = uint(meta.y);
      const posA = positions.element(iIndex).xyz;
      const posB = positions.element(jIndex).xyz;
      const quatA = quaternions.element(iIndex);
      const quatB = quaternions.element(jIndex);

      const rotateByQuat = (v: any, quat: any) => {
        const qv = quat.xyz;
        const qw = quat.w;
        const t = qv.cross(v).mul(2.0);
        return v.add(t.mul(qw)).add(qv.cross(t));
      };

      const armA = pairContacts.element(contactBase.add(uint(CONTACT_RECORD_ARM_A_OFFSET))).xyz;
      const armB = pairContacts.element(contactBase.add(uint(CONTACT_RECORD_ARM_B_OFFSET))).xyz;
      const pointA = posA.add(rotateByQuat(armA, quatA));
      const pointB = posB.add(rotateByQuat(armB, quatB));

      const hiddenPoint = vec3(0.0, -1000000.0, 0.0);
      const dotScale = selectAny(isVisible, float(0.06), float(0.0));
      const pointACenter = selectAny(isVisible, pointA, hiddenPoint);
      const pointBCenter = selectAny(isVisible, pointB, hiddenPoint);

      this.pointAMaterial!.positionNode = positionLocal.mul(dotScale).add(pointACenter);
      this.pointAMaterial!.needsUpdate = true;
      this.pointAMesh!.count = this.capacity;

      this.pointBMaterial!.positionNode = positionLocal.mul(dotScale).add(pointBCenter);
      this.pointBMaterial!.needsUpdate = true;
      this.pointBMesh!.count = this.capacity;

      const segment = pointB.sub(pointA);
      const segmentLength = segment.length().max(float(1e-8));
      const segmentDir = segment.div(segmentLength);
      const refAxis = selectAny(segmentDir.y.abs().greaterThan(float(0.95)), vec3(1.0, 0.0, 0.0), vec3(0.0, 1.0, 0.0));
      const axisX = refAxis.cross(segmentDir).normalize();
      const axisZ = segmentDir.cross(axisX).normalize();
      const connectorCenter = pointA.add(pointB).mul(0.5);
      const connectorRadius = selectAny(isVisible, float(0.009), float(0.0));
      const connectorY = positionLocal.y.mul(selectAny(isVisible, segmentLength, float(0.0)));
      const connectorOffset = axisX.mul(positionLocal.x.mul(connectorRadius))
        .add(segmentDir.mul(connectorY))
        .add(axisZ.mul(positionLocal.z.mul(connectorRadius)));
      const connectorPosition = selectAny(isVisible, connectorCenter.add(connectorOffset), hiddenPoint);

      this.connectorMaterial!.positionNode = connectorPosition;
      this.connectorMaterial!.needsUpdate = true;
      this.connectorMesh!.count = this.capacity;
      this.gpuVisualsBound = true;
    }

    this.connectorMesh!.visible = true;
    this.pointAMesh!.visible = true;
    this.pointBMesh!.visible = true;
  }

  dispose(): void {
    this.connectorMesh?.removeFromParent();
    this.pointAMesh?.removeFromParent();
    this.pointBMesh?.removeFromParent();
    this.connectorMesh?.dispose();
    this.pointAMesh?.dispose();
    this.pointBMesh?.dispose();
    this.connectorGeometry?.dispose();
    this.pointGeometry?.dispose();
    this.connectorMaterial?.dispose();
    this.pointAMaterial?.dispose();
    this.pointBMaterial?.dispose();
    this.connectorMesh = null;
    this.pointAMesh = null;
    this.pointBMesh = null;
    this.connectorGeometry = null;
    this.pointGeometry = null;
    this.connectorMaterial = null;
    this.pointAMaterial = null;
    this.pointBMaterial = null;
  }
}
